import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapPin, Loader2, PaintBucket, Truck, Calendar } from 'lucide-react';
import { toast } from 'sonner';

// Available statuses - SAME for both imports and exports
const STATUSES = [
  'New Shipment',
  'Available for Pickup',
  'On the way to Pickup',
  'Need Appt',
  'In Yard',
  'On the way to Customer',
  'Delivered to Customer',
  'Empty in Customer',
  'On the way to Return',
  'Container Returned',
  'Important Customer',
  'Delivered to Exam Location',
  'Empty Container in Yard',
  'Shipment Closed',
  'Trouble'
];

// Default status colors - SAME for both imports and exports
const STATUS_COLORS = {
  'Empty in Customer': '#FFDAB9',
  'Available for Pickup': '#90EE90',
  'Need Appt': '#1a1a1a',
  'On the way to Customer': '#FFB6C1',
  'Important Customer': '#A0522D',
  'Delivered to Customer': '#FFC0CB',
  'In Yard': '#DDA0DD',
  'Container Returned': '#D3D3D3',
  'On the way to Return': '#AFEEEE',
  'Delivered to Exam Location': '#8B4513',
  'New Shipment': '#E6E6FA',
  'Empty Container in Yard': '#FFFFE0',
  'On the way to Pickup': '#228B22',
  'Shipment Closed': '#ADD8E6',
  'Trouble': '#FA8072'
};

export const LocationChangeModal = ({ 
  open, 
  onOpenChange, 
  shipment, 
  terminals = [],
  yards = [],
  drivers = [],
  onUpdate,
  api,
  entityType = 'shipment' // 'shipment' for imports, 'export' for exports
}) => {
  const [locationType, setLocationType] = useState('terminal');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [localDrivers, setLocalDrivers] = useState([]);

  // Reset all selections when modal opens with new shipment
  useEffect(() => {
    if (open) {
      setSelectedLocation('');
      setSelectedStatus('');
      setSelectedDriver('');
      setSelectedDate('');
      setLocationType('terminal');
    }
  }, [open, shipment?.id]);

  // Fallback: Fetch drivers if props.drivers is empty
  useEffect(() => {
    const fetchDriversIfNeeded = async () => {
      if (open && (!drivers || drivers.length === 0)) {
        console.log('LocationChangeModal: No drivers passed, fetching...');
        try {
          const response = await api?.get('/master/drivers');
          if (response?.data) {
            setLocalDrivers(response.data);
            console.log('LocationChangeModal: Fetched', response.data.length, 'drivers');
          }
        } catch (error) {
          console.error('LocationChangeModal: Failed to fetch drivers:', error);
        }
      } else if (drivers?.length > 0) {
        setLocalDrivers(drivers);
        console.log('LocationChangeModal: Using', drivers.length, 'drivers from props');
      }
    };
    fetchDriversIfNeeded();
  }, [open, drivers, api]);

  const effectiveDrivers = localDrivers.length > 0 ? localDrivers : (drivers || []);

  const handleSubmit = async () => {
    if (!selectedLocation && !selectedStatus && !selectedDriver && !selectedDate) {
      toast.error('Please select at least one change');
      return;
    }

    setLoading(true);
    try {
      const updateData = {};
      const activityChanges = [];
      
      // Update location if selected
      if (selectedLocation && selectedLocation !== 'none') {
        updateData.empty_return_location_id = locationType === 'terminal' ? selectedLocation : null;
        updateData.return_location = locationType === 'terminal' ? selectedLocation : null;
        updateData.yard_id = locationType === 'yard' ? selectedLocation : shipment.yard_id;
        
        const locationName = getLocationName();
        activityChanges.push(`Location changed to ${locationName}`);
      }
      
      // Update status if selected (and not "none")
      if (selectedStatus && selectedStatus !== 'none') {
        updateData.status = selectedStatus;
        activityChanges.push(`Status changed to ${selectedStatus}`);
      }
      
      // Update driver if selected
      if (selectedDriver && selectedDriver !== 'none') {
        updateData.driver_id = selectedDriver;
        const driverName = effectiveDrivers.find(d => d.id === selectedDriver)?.name || selectedDriver;
        updateData.driver_name = driverName;
        activityChanges.push(`Driver assigned: ${driverName}`);
      }

      // Update date if selected
      if (selectedDate) {
        // Add the date to relevant fields based on status
        if (selectedStatus === 'Delivered to Customer' || selectedStatus === 'Delivered to Exam Location') {
          updateData.delivery_date = selectedDate;
          activityChanges.push(`Delivery date: ${selectedDate}`);
        } else if (selectedStatus === 'Container Returned') {
          updateData.return_date = selectedDate;
          activityChanges.push(`Return date: ${selectedDate}`);
        } else if (selectedStatus === 'Available for Pickup') {
          updateData.pickup_date = selectedDate;
          activityChanges.push(`Pickup date: ${selectedDate}`);
        } else {
          // Generic status_date for any other status change
          updateData.status_date = selectedDate;
          activityChanges.push(`Status date: ${selectedDate}`);
        }
      }

      if (Object.keys(updateData).length === 0) {
        toast.error('Please make a selection');
        setLoading(false);
        return;
      }

      // Update based on entity type (shipment or export)
      const endpoint = entityType === 'export' ? `/exports/${shipment.id}` : `/shipments/${shipment.id}`;
      // iter 307.28 — Keep ONLY the PUT inside the save-failure try. If the
      // activity-log POST or any post-save callback (onUpdate/onOpenChange,
      // parent broadcast, fetchShipments) throws AFTER the PUT already
      // returned 200, we must NOT show "Failed to update" — the data IS
      // saved on the server. Separating the paths kills the false-negative
      // toast the user hit when a parent callback (e.g. broadcast on a
      // closed BroadcastChannel) threw synchronously.
      try {
        await api.put(endpoint, updateData);
      } catch (saveError) {
        toast.error(saveError.response?.data?.detail || 'Failed to update');
        return;  // finally will clear loading
      }

      // From here on the save already succeeded — swallow everything.
      if (activityChanges.length > 0) {
        try {
          await api.post('/activity-log', {
            shipment_id: shipment.id,
            container_number: shipment.container_number,
            job_number: shipment.job_number || shipment.booking_number,
            action_type: 'location_status_change',
            description: activityChanges.join('; '),
            changes: {
              location: selectedLocation !== 'none' ? getLocationName() : null,
              status: selectedStatus !== 'none' ? selectedStatus : null,
              driver_id: selectedDriver !== 'none' ? selectedDriver : null,
              driver_name: selectedDriver !== 'none' ? effectiveDrivers.find(d => d.id === selectedDriver)?.name : null,
              date: selectedDate || null
            }
          });
        } catch (activityError) {
          console.error('Failed to log activity (save still succeeded):', activityError);
        }
      }

      toast.success('Updated successfully');
      try { onUpdate && onUpdate(); } catch (cbErr) {
        console.error('onUpdate callback threw (save still succeeded):', cbErr);
      }
      try { onOpenChange && onOpenChange(false); } catch (cbErr) {
        console.error('onOpenChange callback threw (save still succeeded):', cbErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedLocation('');
    setSelectedStatus('');
    setSelectedDriver('');
    setSelectedDate('');
    setLocationType('terminal');
    onOpenChange(false);
  };

  // Get location name for display
  const getLocationName = () => {
    if (locationType === 'terminal') {
      return terminals.find(t => t.id === selectedLocation)?.name || '';
    }
    return yards.find(y => y.id === selectedLocation)?.name || '';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-slate-900 border-slate-800 sm:max-w-[500px]"
        aria-describedby="location-change-description"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-500" />
            Change Location / Status / Driver
          </DialogTitle>
          <DialogDescription id="location-change-description" className="text-slate-400">
            Update for {entityType === 'export' ? 'booking' : 'container'} <span className="font-mono text-white">{shipment?.container_number || shipment?.booking_number}</span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Status Selection with Color Preview */}
          <div className="space-y-2">
            <Label className="text-slate-300 flex items-center gap-2">
              <PaintBucket className="h-4 w-4" />
              Status (with color)
            </Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="bg-slate-950 border-slate-700 text-white">
                <SelectValue placeholder="Select status..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 max-h-[250px]">
                <SelectItem value="none" className="text-slate-400">
                  -- No change --
                </SelectItem>
                {STATUSES.map(status => (
                  <SelectItem 
                    key={status} 
                    value={status}
                    className="text-slate-200"
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded border border-slate-600"
                        style={{ backgroundColor: STATUS_COLORS[status] || '#94a3b8' }}
                      />
                      {status}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedStatus && selectedStatus !== 'none' && (
              <div 
                className="p-2 rounded text-sm font-medium"
                style={{ 
                  backgroundColor: STATUS_COLORS[selectedStatus],
                  color: ['Need Appt', 'Important Customer', 'Delivered to Exam Location', 'On the way to Pickup'].includes(selectedStatus) ? '#fff' : '#1a1a1a'
                }}
              >
                Preview: {selectedStatus}
              </div>
            )}
          </div>

          {/* Driver Selection */}
          <div className="space-y-2 border-t border-slate-700 pt-4">
            <Label className="text-slate-300 flex items-center gap-2">
              <Truck className="h-4 w-4" />
              Assign Driver ({effectiveDrivers.length} available)
            </Label>
            <Select 
              value={selectedDriver} 
              onValueChange={setSelectedDriver}
              key={`driver-select-${effectiveDrivers.length}`}
            >
              <SelectTrigger className="bg-slate-950 border-slate-700 text-white">
                <SelectValue placeholder="Select driver..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 max-h-[200px]">
                <SelectItem value="none" className="text-slate-400">
                  -- No change --
                </SelectItem>
                {effectiveDrivers.length > 0 ? (
                  effectiveDrivers.map(driver => (
                    <SelectItem 
                      key={driver.id} 
                      value={driver.id}
                      className="text-slate-200"
                    >
                      {driver.name} {driver.code ? `(${driver.code})` : ''}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-drivers" disabled className="text-slate-500">
                    Loading drivers...
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {selectedDriver && selectedDriver !== 'none' && (
              <div className="p-2 bg-green-900/30 border border-green-700 rounded text-sm text-green-300">
                Driver: {effectiveDrivers.find(d => d.id === selectedDriver)?.name}
              </div>
            )}
          </div>

          {/* Date Selection - NEW */}
          <div className="space-y-2 border-t border-slate-700 pt-4">
            <Label className="text-slate-300 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date (Optional)
            </Label>
            <Input 
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-slate-950 border-slate-700 text-white"
            />
            {selectedDate && (
              <p className="text-xs text-slate-400">
                {selectedStatus === 'Delivered to Customer' && 'Will set as Delivery Date'}
                {selectedStatus === 'Container Returned' && 'Will set as Return Date'}
                {selectedStatus === 'Available for Pickup' && 'Will set as Pickup Date'}
                {!['Delivered to Customer', 'Container Returned', 'Available for Pickup'].includes(selectedStatus) && 'Will set as Status Change Date'}
              </p>
            )}
          </div>

          <div className="border-t border-slate-700 pt-4">
            <Label className="text-slate-300 mb-2 block">Location (optional)</Label>
            
            {/* Location Type Selection */}
            <div className="space-y-2 mb-3">
              <Select value={locationType} onValueChange={(v) => {
                setLocationType(v);
                setSelectedLocation('');
              }}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="terminal" className="text-slate-200">Terminal / Port</SelectItem>
                  <SelectItem value="yard" className="text-slate-200">Yard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Location Selection */}
            <div className="space-y-2">
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white">
                  <SelectValue placeholder={`Choose a ${locationType}...`} />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-[200px]">
                  <SelectItem value="none" className="text-slate-400">
                    -- No change --
                  </SelectItem>
                  {locationType === 'terminal' ? (
                    terminals.map(terminal => (
                      <SelectItem 
                        key={terminal.id} 
                        value={terminal.id}
                        className="text-slate-200"
                      >
                        {terminal.name} {terminal.code ? `(${terminal.code})` : ''}
                      </SelectItem>
                    ))
                  ) : (
                    yards.map(yard => (
                      <SelectItem 
                        key={yard.id} 
                        value={yard.id}
                        className="text-slate-200"
                      >
                        {yard.name} {yard.code ? `(${yard.code})` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preview */}
          {selectedLocation && selectedLocation !== 'none' && (
            <div className="p-3 bg-slate-800 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-400 mb-1">New Location:</p>
              <p className="text-white font-medium">{getLocationName()}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              ((!selectedLocation || selectedLocation === 'none') && 
               (!selectedStatus || selectedStatus === 'none') && 
               (!selectedDriver || selectedDriver === 'none') &&
               !selectedDate) || 
              loading
            }
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="save-location-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Update'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationChangeModal;
